import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import Heading from '@atlaskit/heading/heading';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Image from '@atlaskit/image/image';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { requestLoomMeetingNudgeReminder } from '../../../reminder-event';
import AiNotesIcon from './assets/ai-notes.svg';
import { NotInterestedConfirmationFlag } from './NotInterestedConfirmationFlag';
import { ReminderConfirmationFlag } from './ReminderConfirmationFlag';

type MeetingNudgeBannerProps = {
	cloudId: string;
	handleOnDismissed: () => void;
};

type ConfirmationFlag = 'notInterested' | 'remindMe' | null;

const getClickEvent = (
	clickIdentifier: 'connect-calendar' | 'not-interested' | 'remind-me' | 'why-am-i-seeing-this',
	actionType: 'acknowledge' | 'dismiss' | 'open',
) => ({
	messageEventType: 'clicked' as const,
	clickedElement: {
		clickIdentifier,
		clickedElement: 'button' as const,
		buttonType: actionType === 'open' ? ('primary' as const) : ('secondary' as const),
	},
	action: { actionType },
	additionalMetadataFromFrontend: {},
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
	},
	action: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		display: 'inline-flex',
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	aiNotesIcon: {
		height: '40px',
		width: '40px',
	},
});

export const MeetingNudgeBanner = ({
	cloudId,
	handleOnDismissed,
}: MeetingNudgeBannerProps): JSX.Element => {
	const intl = useIntl();
	const [confirmationFlag, setConfirmationFlag] = useState<ConfirmationFlag>(null);
	const [isWhyModalOpen, setIsWhyModalOpen] = useState(false);
	const hasFinishedConfirmationRef = useRef(false);
	const { messageAcknowledged, messageDismissed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const handleConnectCalendar = () => {
		void messageInstanceClicked(getClickEvent('connect-calendar', 'open'));
		void messageDismissed();
		openLoomMeetingRecordings({
			bridge: 'loom-meeting-nudge-sidenav',
			cloudId,
			interactionSource: 'confluence',
			loomSource: 'confluence-nudge-sidenav',
		});
		handleOnDismissed();
	};

	const handleRemindMe = () => {
		void messageInstanceClicked(getClickEvent('remind-me', 'dismiss'));
		void messageDismissed();
		requestLoomMeetingNudgeReminder();
		hasFinishedConfirmationRef.current = false;
		setConfirmationFlag('remindMe');
	};

	const handleNotInterested = () => {
		void messageInstanceClicked(getClickEvent('not-interested', 'dismiss'));
		hasFinishedConfirmationRef.current = false;
		setConfirmationFlag('notInterested');
	};

	const finishConfirmation = (commitDismissal: boolean) => {
		if (hasFinishedConfirmationRef.current) {
			return;
		}
		hasFinishedConfirmationRef.current = true;
		if (commitDismissal) {
			void messageDismissed();
		}
		setConfirmationFlag(null);
		handleOnDismissed();
	};

	const handleWhyAmISeeingThis = () => {
		void messageInstanceClicked(getClickEvent('why-am-i-seeing-this', 'acknowledge'));
		void messageAcknowledged();
		setIsWhyModalOpen(true);
	};

	return (
		<>
			{confirmationFlag === null && (
				<Box xcss={styles.container}>
					<Stack space="space.100">
						<Inline alignBlock="start" spread="space-between">
							<Box xcss={styles.aiNotesIcon}>
								<Image
									src={AiNotesIcon}
									alt=""
									width="40"
									height="40"
									testId="loom-meeting-nudge-ai-notes-icon"
								/>
							</Box>
							<DropdownMenu
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										{...triggerProps}
										appearance="subtle"
										icon={ShowMoreHorizontalIcon}
										label={intl.formatMessage({
											id: 'post-office.twc-loom-meeting-nudge-sidenav.more-actions',
											defaultMessage: 'More actions',
											description: 'Label for the Loom meeting nudge options menu',
										})}
										ref={triggerRef}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={handleNotInterested}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.not-interested"
											defaultMessage="Not interested"
											description="Action to permanently turn off Loom meeting recording suggestions"
										/>
									</DropdownItem>
									<DropdownItem onClick={handleWhyAmISeeingThis}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.why-am-i-seeing-this"
											defaultMessage="Why am I seeing this?"
											description="Action to explain why the Loom meeting nudge was shown"
										/>
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</Inline>

						<Heading color="color.text" size="xsmall">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.heading"
								defaultMessage="Turn meetings into action"
								description="Heading for a side navigation banner promoting Loom meeting recordings"
							/>
						</Heading>
						<Text as="p" color="color.text.subtle">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.body"
								defaultMessage="Loom captures action items in Confluence and Jira so you don’t have to."
								description="Body copy for a side navigation banner promoting Loom meeting recordings"
							/>
						</Text>
						<Inline alignBlock="center" space="space.100">
							<Pressable onClick={handleConnectCalendar} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.connect-calendar"
										defaultMessage="Connect calendar"
										description="Action to open Loom's calendar connection flow"
									/>
								</Text>
							</Pressable>
							<span aria-hidden="true">·</span>
							<Pressable onClick={handleRemindMe} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.remind-me"
										defaultMessage="Remind me"
										description="Action to defer the Loom calendar reminder until the next publish or live-page load"
									/>
								</Text>
							</Pressable>
						</Inline>
					</Stack>
				</Box>
			)}
			{confirmationFlag === 'remindMe' && (
				<ReminderConfirmationFlag onDismissed={() => finishConfirmation(false)} />
			)}
			{confirmationFlag === 'notInterested' && (
				<NotInterestedConfirmationFlag
					onDismissed={() => finishConfirmation(true)}
					onUndo={() => finishConfirmation(false)}
				/>
			)}
			<ModalTransition>
				{isWhyModalOpen && (
					<ModalDialog onClose={() => setIsWhyModalOpen(false)}>
						<ModalHeader>
							<ModalTitle>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.title"
									defaultMessage="Why am I seeing this?"
									description="Title for an explanation of why the Loom meeting nudge was shown"
								/>
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.body"
								defaultMessage="This suggestion appears because this page includes content related to meetings."
								description="Explanation of the meeting-content signal used to show the Loom nudge"
							/>
						</ModalBody>
						<ModalFooter>
							<Button appearance="primary" onClick={() => setIsWhyModalOpen(false)}>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.close"
									defaultMessage="Got it"
									description="Action to close the Loom meeting nudge explanation"
								/>
							</Button>
						</ModalFooter>
					</ModalDialog>
				)}
			</ModalTransition>
		</>
	);
};
